import { supabase } from '../../../services/supabaseClient';

const TABLE_POSTS = 'mf_blog_posts';
const TABLE_TOPICS = 'mf_blog_topics';
const TABLE_CONFIG = 'mf_blog_agent_config';
const STORAGE_BUCKET = 'mf-blog';

// ===== STORAGE =====

export function getBlogImageUrl(path) {
  if (!path) return '';
  // se já é URL completa, retorna como está
  if (path.startsWith('http')) return path;
  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
  return data?.publicUrl || '';
}

export async function uploadBlogImage(file, folder = 'capas') {
  const fileName = `${folder}/${Date.now()}-${file.name}`;
  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(fileName, file, { cacheControl: '3600', upsert: false });

  if (error) {
    console.error('Erro ao subir imagem do blog:', error);
    return null;
  }
  return getBlogImageUrl(data.path);
}

// ===== READ — POSTS =====

export async function getPublishedPosts({ limit, offset = 0, categoria } = {}) {
  let query = supabase
    .from(TABLE_POSTS)
    .select('*')
    .eq('status', 'publicado')
    .order('published_at', { ascending: false });

  if (categoria) query = query.eq('categoria', categoria);
  if (limit) query = query.range(offset, offset + limit - 1);

  const { data, error } = await query;
  if (error) {
    console.error('Erro ao buscar posts:', error);
    return [];
  }
  return data || [];
}

export async function getFeaturedPost() {
  const { data, error } = await supabase
    .from(TABLE_POSTS)
    .select('*')
    .eq('status', 'publicado')
    .order('published_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Erro ao buscar post em destaque:', error);
    return null;
  }
  return data;
}

export async function getPostBySlug(slug) {
  const { data, error } = await supabase
    .from(TABLE_POSTS)
    .select('*')
    .eq('slug', slug)
    .eq('status', 'publicado')
    .maybeSingle();

  if (error) {
    console.error('Erro ao buscar post:', error);
    return null;
  }
  return data;
}

export async function getRelatedPosts(currentPostId, { categoria, bairro, limit = 3 } = {}) {
  let query = supabase
    .from(TABLE_POSTS)
    .select('id, slug, titulo, resumo, imagem_capa, categoria, published_at')
    .eq('status', 'publicado')
    .neq('id', currentPostId)
    .order('published_at', { ascending: false })
    .limit(limit);

  // prioridade: mesmo bairro > mesma categoria > qualquer
  if (bairro) query = query.eq('bairro_relacionado', bairro);
  else if (categoria) query = query.eq('categoria', categoria);

  const { data, error } = await query;
  if (error) {
    console.error('Erro ao buscar relacionados:', error);
    return [];
  }
  return data || [];
}

export async function getPostsByCategoria(categoria, { limit = 24 } = {}) {
  return getPublishedPosts({ limit, categoria });
}

export async function getAllCategorias() {
  const { data, error } = await supabase
    .from(TABLE_POSTS)
    .select('categoria')
    .eq('status', 'publicado')
    .not('categoria', 'is', null);

  if (error) return [];
  const counts = {};
  (data || []).forEach((row) => {
    if (row.categoria) counts[row.categoria] = (counts[row.categoria] || 0) + 1;
  });
  return Object.entries(counts).map(([slug, count]) => ({ slug, count }));
}

export async function incrementPostViews(postId) {
  // best-effort, não bloqueia render
  try {
    await supabase.rpc('increment_post_views', { post_id: postId });
  } catch (_) {
    // se RPC não existir, faz fallback
    const { data } = await supabase
      .from(TABLE_POSTS)
      .select('views')
      .eq('id', postId)
      .maybeSingle();
    if (data) {
      await supabase
        .from(TABLE_POSTS)
        .update({ views: (data.views || 0) + 1 })
        .eq('id', postId);
    }
  }
}

// ===== WRITE — POSTS (admin) =====

export async function createPost(postData) {
  const { data, error } = await supabase
    .from(TABLE_POSTS)
    .insert(postData)
    .select()
    .single();

  if (error) {
    console.error('Erro ao criar post:', error);
    return { data: null, error };
  }
  return { data, error: null };
}

export async function updatePost(id, updates) {
  const { data, error } = await supabase
    .from(TABLE_POSTS)
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Erro ao atualizar post:', error);
    return { data: null, error };
  }
  return { data, error: null };
}

export async function publishPost(id) {
  return updatePost(id, {
    status: 'publicado',
    published_at: new Date().toISOString(),
  });
}

export async function deletePost(id) {
  const { error } = await supabase.from(TABLE_POSTS).delete().eq('id', id);
  return { error };
}

export async function getAllPostsAdmin({ status } = {}) {
  let query = supabase
    .from(TABLE_POSTS)
    .select('*')
    .order('updated_at', { ascending: false });

  if (status) query = query.eq('status', status);

  const { data, error } = await query;
  if (error) {
    console.error('Erro ao listar posts admin:', error);
    return [];
  }
  return data || [];
}

// ===== TOPICS =====

export async function listTopics({ status } = {}) {
  let query = supabase
    .from(TABLE_TOPICS)
    .select('*')
    .order('prioridade', { ascending: false })
    .order('created_at', { ascending: false });
  if (status) query = query.eq('status', status);
  const { data, error } = await query;
  if (error) return [];
  return data || [];
}

export async function createTopic(topicData) {
  const { data, error } = await supabase
    .from(TABLE_TOPICS)
    .insert(topicData)
    .select()
    .single();
  return { data, error };
}

export async function updateTopic(id, updates) {
  const { data, error } = await supabase
    .from(TABLE_TOPICS)
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

export async function deleteTopic(id) {
  return supabase.from(TABLE_TOPICS).delete().eq('id', id);
}

// ===== AGENT CONFIG =====

export async function getAllAgentConfig() {
  const { data, error } = await supabase
    .from(TABLE_CONFIG)
    .select('*')
    .order('category')
    .order('display_order');
  if (error) return [];
  return data || [];
}

export async function getAgentConfigValue(key) {
  const { data, error } = await supabase
    .from(TABLE_CONFIG)
    .select('value')
    .eq('key', key)
    .maybeSingle();
  if (error) return null;
  return data?.value || null;
}

export async function updateAgentConfig(key, value) {
  const { data, error } = await supabase
    .from(TABLE_CONFIG)
    .update({ value, updated_at: new Date().toISOString() })
    .eq('key', key)
    .select()
    .single();
  return { data, error };
}

export async function getAgentConfigHistory(key, limit = 10) {
  const { data, error } = await supabase
    .from('mf_blog_agent_config_history')
    .select('*')
    .eq('key', key)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return data || [];
}

// ===== AGENT DISPATCH =====

/**
 * Dispara a task gerar-artigo-blog do trigger.dev via /api/blog-trigger.
 * Usado pelo botão "Gerar agora" no admin.
 */
export async function triggerGerarArtigo({ topic_id, tema, angulo, bairro_alvo, categoria, palavra_chave_principal } = {}) {
  const res = await fetch('/api/blog-trigger', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      task: 'gerar-artigo-blog',
      payload: { topic_id, tema, angulo, bairro_alvo, categoria, palavra_chave_principal },
    }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data, status: res.status };
}

/**
 * Dispara a task descobrir-pautas-blog (sugere novas pautas).
 */
export async function triggerDescobrirPautas({ count = 8, auto_approve = false } = {}) {
  const res = await fetch('/api/blog-trigger', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      task: 'descobrir-pautas-blog',
      payload: { count, auto_approve },
    }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data, status: res.status };
}

// ===== AGENT CONFIG (upsert) =====
// getAgentConfigValue já existe acima — esta versão faz upsert (cria se não existir)

export async function setAgentConfigValue(key, value) {
  // Usa o endpoint serverless /api/blog-config pq a tabela tem RLS que
  // bloqueia escrita com a chave anon. O endpoint valida com a senha
  // admin (mesma do login) e usa service_role do server pra fazer o upsert.
  try {
    const res = await fetch('/api/blog-config', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-admin-pass': 'Mf$$26',
      },
      body: JSON.stringify({ key, value }),
    });
    if (!res.ok) {
      const detail = await res.json().catch(() => ({}));
      console.error('Erro ao salvar config:', detail);
      return false;
    }
    return true;
  } catch (e) {
    console.error('Erro ao salvar config:', e);
    return false;
  }
}

// ===== HELPERS =====

export const CATEGORIA_LABELS = {
  'guia-bairro': 'Guia de Bairro',
  'financeiro': 'Financeiro',
  'investimento': 'Investimento',
  'mercado': 'Mercado',
  'processo': 'Processo',
};

export function getCategoriaLabel(slug) {
  return CATEGORIA_LABELS[slug] || slug;
}

export function formatPostDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function estimateReadingTime(markdown) {
  if (!markdown) return 1;
  const words = markdown.split(/\s+/).length;
  return Math.max(1, Math.round(words / 220));
}

// extrai os H2s do markdown pra montar o sumário (TOC)
export function extractTOC(markdown) {
  if (!markdown) return [];
  const lines = markdown.split('\n');
  const toc = [];
  lines.forEach((line) => {
    const m2 = line.match(/^##\s+(.+)$/);
    if (m2) {
      const title = m2[1].trim();
      const id = title
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      toc.push({ id, title });
    }
  });
  return toc;
}
